import { z } from 'zod';
import { cardListSchema, cardTitleSchema } from './card';
import { ulidSchema } from './id';

export const listCardSortModeSchema = z.enum([
  'created_newest',
  'created_oldest',
  'due_date',
  'title',
]);

export const listCardSortBodySchema = z.strictObject({
  mode: listCardSortModeSchema,
});

export type ListCardSortBody = z.infer<typeof listCardSortBodySchema>;

export const listCardMoveAllBodySchema = z.strictObject({
  listId: ulidSchema,
});

export type ListCardMoveAllBody = z.infer<typeof listCardMoveAllBodySchema>;

export function parsePastedCardTitles(text: string): string[] {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export const listCardBulkBodySchema = z
  .strictObject({
    text: z.string(),
    previousCardId: ulidSchema.nullable().default(null),
    nextCardId: ulidSchema.nullable().default(null),
  })
  .superRefine((value, context) => {
    const titles = parsePastedCardTitles(value.text);
    if (titles.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['text'],
        message: 'Paste at least one nonblank line.',
      });
      return;
    }

    for (const [index, title] of titles.entries()) {
      if (!cardTitleSchema.safeParse(title).success) {
        context.addIssue({
          code: 'custom',
          path: ['text'],
          message: `Line ${index + 1} is not a valid card title.`,
        });
      }
    }
  });

export type ListCardBulkBody = z.infer<typeof listCardBulkBodySchema>;

export const listCardsChangedSchema = z.object({
  cards: cardListSchema,
  count: z.number().int().nonnegative(),
});

export type ListCardsChanged = z.infer<typeof listCardsChangedSchema>;

export const boardCapacityKindSchema = z.enum([
  'open_cards',
  'total_cards',
  'open_lists',
  'total_lists',
  'attachments_per_card',
  'checklists_per_card',
  'checklists_per_board',
  'labels_per_board',
  'members_per_board',
]);

export type BoardCapacityKind = z.infer<typeof boardCapacityKindSchema>;

export const boardCapacityStatusSchema = z.enum(['ok', 'warning', 'at_limit']);

export const boardCapacityEntrySchema = z.object({
  kind: boardCapacityKindSchema,
  current: z.number().int().nonnegative(),
  warningAt: z.number().int().positive(),
  limit: z.number().int().positive(),
  status: boardCapacityStatusSchema,
});

export const boardCapacitySchema = z.object({
  boardId: ulidSchema,
  capacities: z.array(boardCapacityEntrySchema),
});

export type BoardCapacity = z.infer<typeof boardCapacitySchema>;
